import { ArrowRight } from "@tamagui/lucide-icons";
import { useContext, useState } from "react";
import {
  Directions,
  Gesture,
  GestureDetector,
} from "react-native-gesture-handler";
import Animated, { SlideInUp, SlideOutUp } from "react-native-reanimated";
import { Theme, YStack, getTokenValue } from "tamagui";

import { useGetClinicStatusForPatient } from "@/api/generated";
import { EncounterStartType } from "@/api/generated/models/EncounterStartType";
import { useGetAllConversations } from "@/api/queries/conversation";
import ActionCard from "@/components/ActionCard";
import { Button } from "@/components/Button";
import { LayoutContext } from "@/components/LayoutProvider";
import Logo from "@/components/Logo";
import { Heading, Text } from "@/components/Typography";
import { medicalGroupName } from "@/config/brand";
import CuraiConsentRedirect from "@/conversation/sidebar/CuraiConsentRedirect";
import { useCurrentConversationId } from "@/hooks/conversation";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useAuthStore } from "@/store";
import { getShadow } from "@/theme/helpers";

export const VISIT_STATUS = {
  OPEN: "open",
  PENDING: "pending",
  UNREAD: "unread",
  RESUMABLE: "resumable",
  NONE: "none",
};

export const useVisitStatus = () => {
  const { isAuthInitialized } = useAuthStore();
  const { data: clinicStatus } = useGetClinicStatusForPatient({
    query: { enabled: isAuthInitialized },
  });
  const { data: conversationHistory } = useGetAllConversations();
  const { data: conversations } = useGetAllConversations();
  const currentConversationId = useCurrentConversationId();
  const unreadVisit = conversationHistory?.conversations.find(
    (convo) => convo.has_unread && convo.curai_conversation_id,
  );
  if (unreadVisit) {
    if (unreadVisit?.curai_conversation_id === currentConversationId) {
      return {
        status: VISIT_STATUS.NONE,
        conversationId: null,
      };
    }
    return {
      status: VISIT_STATUS.UNREAD,
      conversationId: unreadVisit.curai_conversation_id,
    };
  }

  if (
    clinicStatus?.latest_visit?.type === EncounterStartType.ASYNC_RX ||
    clinicStatus?.latest_visit?.type === EncounterStartType.RX_RENEWAL ||
    clinicStatus?.latest_visit?.type === EncounterStartType.ASYNC_DX
  ) {
    return {
      status: VISIT_STATUS.NONE,
      conversationId: null,
    };
  }

  if (
    clinicStatus?.latest_visit?.status === "in-progress" ||
    clinicStatus?.latest_visit?.status === "pending"
  ) {
    const currentClinicConvoId = clinicStatus.conversation?.id;
    const currentClinicsNoriConvoId = conversations?.conversations.find(
      (convo) => convo.curai_conversation_id === currentClinicConvoId,
    )?.id;
    if (
      // Don't show the indicator if we're already on the current clinic conversation,
      // or if we're on the Nori conversation associated with the current clinic conversation
      currentClinicConvoId === currentConversationId ||
      (Boolean(currentClinicsNoriConvoId) &&
        currentClinicsNoriConvoId === currentConversationId)
    ) {
      return {
        status: VISIT_STATUS.NONE,
        conversationId: null,
      };
    }
    return {
      status:
        clinicStatus.latest_visit.status === "pending"
          ? VISIT_STATUS.PENDING
          : VISIT_STATUS.OPEN,
      conversationId: currentClinicConvoId,
      encounterType: clinicStatus.latest_visit.type ?? null,
    };
  }

  if (clinicStatus?.latest_visit?.can_be_resumed) {
    return {
      status: VISIT_STATUS.RESUMABLE,
      conversationId: clinicStatus.conversation?.id,
    };
  }

  return {
    status: VISIT_STATUS.NONE,
    conversationId: null,
  };
};

export const VisitStatusIndicatorSidebar = () => {
  const { status, conversationId } = useVisitStatus();
  const router = usePropagatedRouter();
  const { setModalContent } = useContext(LayoutContext);

  if (status === VISIT_STATUS.NONE || status === VISIT_STATUS.PENDING) {
    return null;
  }

  let title = `Your ${medicalGroupName} session is in progress`;
  let subtitle =
    "Your clinician is waiting. Tap to continue your conversation.";
  let dataTrackingId = "visit-status-in-progress";
  let onPress = () => router.navigate(`/conversation/${conversationId}`);
  if (status === VISIT_STATUS.UNREAD) {
    title = "New message from your clinician";
    subtitle = "Tap to read the message."; // TODO: retrieve clinician name from status endpoint
    dataTrackingId = "visit-status-unread-message";
  } else if (status === VISIT_STATUS.RESUMABLE) {
    title = `Your ${medicalGroupName} session is paused`;
    subtitle = "Your clinician is waiting. Tap to resume your conversation.";
    dataTrackingId = "visit-status-resume-visit";
    onPress = () =>
      setModalContent(() => (props: any) => (
        <CuraiConsentRedirect
          resume
          conversationId={conversationId as string}
          {...props}
        />
      ));
  }

  return (
    <Theme name="clinic">
      <YStack
        borderRadius="$150"
        padding="$200"
        gap="$150"
        backgroundColor="$fillCallout"
        marginBottom="$500"
        marginRight="$100"
        {...getShadow()}
      >
        <Heading size="$md">{title}</Heading>
        <Text size="$sm" lineHeight="$md" color="$textSecondary">
          {subtitle}
        </Text>
        <Button
          iconAfter={ArrowRight}
          onPress={onPress}
          dataTrackingId={dataTrackingId}
          width="100%"
        >
          Resume visit
        </Button>
      </YStack>
    </Theme>
  );
};

export const VisitStatusIndicatorPopup = () => {
  const { status, conversationId } = useVisitStatus();
  const { lg, setModalContent } = useContext(LayoutContext);
  const [open, setOpen] = useState(true);
  const router = usePropagatedRouter();

  if (status === VISIT_STATUS.NONE || status === VISIT_STATUS.PENDING) {
    return null;
  }

  let title = `Your ${medicalGroupName} session is in progress`;
  let dataTrackingId = "visit-status-in-progress";
  let onPress = () => router.navigate(`/conversation/${conversationId}`);
  if (status === VISIT_STATUS.UNREAD) {
    title = "New message from your clinician";
    dataTrackingId = "visit-status-unread-message";
  } else if (status === VISIT_STATUS.RESUMABLE) {
    title = "Still need to talk to a clinician?";
    dataTrackingId = "visit-status-resume-visit";
    onPress = () =>
      setModalContent(() => (props: any) => (
        <CuraiConsentRedirect
          resume
          conversationId={conversationId as string}
          {...props}
        />
      ));
  }

  return (
    open && (
      <Animated.View
        style={{
          position: "absolute",
          right: 0,
          left: lg ? 40 : 0,
          top: 0,
          zIndex: 2,
          alignItems: "center",
          paddingHorizontal: getTokenValue("$200"),
        }}
        entering={SlideInUp.duration(500)}
        exiting={SlideOutUp.duration(500)}
      >
        <GestureDetector
          gesture={Gesture.Fling()
            .direction(Directions.UP)
            .onStart(() => setOpen(false))}
        >
          <ActionCard
            title={title}
            description=""
            icon={<Logo.Only />}
            dataTrackingId={dataTrackingId}
            onPress={() => {
              setOpen(false);
              onPress();
            }}
            marginHorizontal="$200"
            marginVertical="$200"
            width="100%"
            maxWidth="$constraint"
          />
        </GestureDetector>
      </Animated.View>
    )
  );
};
